import { Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const LOGO_URL = "/tickets/cuchara-grande-logo-v3.png";
const TICKET_BG_URL = "/tickets/cuchara-grande-ticket-bg-v3.png";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function drawFittedText(context, text, x, y, maxWidth, initialSize, weight = 600) {
  let size = initialSize;
  do {
    context.font = `${weight} ${size}px Arial, sans-serif`;
    size -= 2;
  } while (context.measureText(text).width > maxWidth && size > 18);
  context.fillText(text, x, y);
}

export default function DigitalTicket({ ticket }) {
  const canvasId = `qr-${ticket.id}`;
  const active = ticket.status === "valid";
  const statusLabel = { valid: "VIGENTE", used: "UTILIZADO", cancelled: "CANCELADO" }[ticket.status] || String(ticket.status || "").toUpperCase();

  const downloadTicket = async () => {
    const qrCanvas = document.getElementById(canvasId);
    if (!qrCanvas || !active) return;
    const [background, logo] = await Promise.all([loadImage(TICKET_BG_URL), loadImage(LOGO_URL)]);
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 675;
    const context = canvas.getContext("2d");
    context.drawImage(background, 0, 0, canvas.width, canvas.height);
    context.drawImage(logo, 58, 38, 390, 178);

    context.fillStyle = "#243619";
    context.font = "700 18px Arial, sans-serif";
    context.fillText("BOLETO OFICIAL", 68, 265);
    context.fillStyle = "#17210F";
    drawFittedText(context, ticket.ticket_type_name, 68, 315, 690, 38, 700);
    context.fillStyle = "#5A664B";
    context.font = "700 15px Arial, sans-serif";
    context.fillText("TITULAR", 68, 382);
    context.fillStyle = "#17210F";
    drawFittedText(context, ticket.buyer_name, 68, 425, 690, 31, 600);
    context.fillStyle = "#5A664B";
    context.font = "700 15px Arial, sans-serif";
    context.fillText("FOLIO", 68, 490);
    context.fillStyle = "#17210F";
    context.font = "700 28px monospace";
    context.fillText(ticket.code, 68, 530);
    context.fillStyle = "#5A664B";
    context.font = "400 16px Arial, sans-serif";
    context.fillText("QR personal de un solo uso. No lo compartas.", 68, 592);

    context.drawImage(qrCanvas, 905, 130, 220, 220);
    context.fillStyle = "#243619";
    context.textAlign = "center";
    context.font = "700 18px monospace";
    context.fillText(ticket.code, 1015, 395);
    context.font = "700 15px Arial, sans-serif";
    context.fillText(statusLabel, 1015, 438);
    context.font = "400 13px Arial, sans-serif";
    context.fillText("Presenta este código en el acceso", 1015, 485);
    context.textAlign = "left";

    const link = document.createElement("a");
    link.download = `Boleto-${ticket.code}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <article className="relative overflow-hidden rounded-lg border border-[#cfc8b8] bg-white shadow-sm break-inside-avoid">
      <div className="relative bg-[#A9D8C5] px-5 py-4">
        <img src={LOGO_URL} alt="Cuchara Grande 11° Edición" className="h-auto w-full max-w-[360px]" />
        <span className={`absolute right-3 top-3 border px-2 py-1 text-[10px] font-semibold tracking-wider ${active ? "border-green-900 bg-white text-green-900" : "border-red-800 bg-white text-red-800"}`}>{statusLabel}</span>
      </div>

      <div className="grid sm:grid-cols-[1fr_210px] items-center gap-5 p-5">
        <div className="text-center sm:text-left min-w-0">
          <div className="overline text-[var(--brand-muted)]">Boleto</div>
          <div className="font-serif-cg text-xl mt-1">{ticket.ticket_type_name}</div>
          <div className="overline text-[var(--brand-muted)] mt-4">Titular</div>
          <div className="font-serif-cg text-xl mt-1 truncate">{ticket.buyer_name}</div>
          <div className="overline text-[var(--brand-muted)] mt-4">Folio</div>
          <div className="font-mono text-sm font-semibold mt-1">{ticket.code}</div>
          <p className="text-xs text-[var(--brand-muted)] mt-4 leading-relaxed">{active ? "QR personal de un solo uso. No lo compartas." : ticket.status === "used" ? "Este boleto ya fue utilizado." : "Este boleto fue cancelado por la administración."}</p>
        </div>
        <div className="relative bg-white p-2 border border-[var(--brand-border)] w-fit mx-auto">
          <QRCodeCanvas id={canvasId} value={ticket.qr_token} size={185} level="M" marginSize={2} />
          {!active ? <div className="absolute inset-2 grid place-items-center bg-white/90"><strong className="-rotate-12 border-2 border-current px-3 py-1 text-sm tracking-widest text-red-700">{statusLabel}</strong></div> : null}
        </div>
      </div>

      <div className="border-t border-dashed border-[var(--brand-border)] px-5 py-4">
        <button type="button" onClick={downloadTicket} disabled={!active} className="btn-secondary-cg text-xs disabled:cursor-not-allowed disabled:opacity-40">
          <Download size={15} /> Descargar boleto
        </button>
      </div>
    </article>
  );
}
